/**
 * Integrations endpoints — the Slack workspaces Alveary may post
 * into and the channels under each (PRD-0359 D-8, D-14), the Jenkins
 * controllers whose failed builds Alveary records, with their job bindings,
 * credentials, publisher setup and build history (PRD-0402 D-23), and the
 * capability, communication and receipt reads every provider answers through
 * (PRD-0401 D-12, D-20; PRD-0402 D-22).
 *
 * Every path carries the trailing slash the router declares, because FastAPI
 * 307-redirects a missing one before auth runs and the client would then
 * follow an unauthenticated redirect into a 401.
 *
 * The viewer read and the administrator read of a connection are separate
 * calls against separate routes, because that is where the boundary is drawn:
 * only `/admin/` returns Slack's secret name and granted scopes, or a Jenkins
 * instance's setup and traffic, and only an administrator may reach it. No call
 * here reaches Slack or Jenkins — Verify asks the job runner for a check and
 * answers `pending` at once, and every Jenkins event arrives from the
 * controller itself.
 */

import type {
  IntegrationCapabilityFilters,
  IntegrationCapabilityListResponse,
  IntegrationCommunicationFilters,
  IntegrationCommunicationListResponse,
  IntegrationReceipt,
  IntegrationReceiptFilters,
  IntegrationReceiptListResponse,
  IntegrationReceiptReplayRequest,
} from '@/types/integrations/foundation'
import type {
  JenkinsBindingFilters,
  JenkinsBuildEvent,
  JenkinsBuildEventListResponse,
  JenkinsBuildFilters,
  JenkinsConnection,
  JenkinsConnectionAdmin,
  JenkinsConnectionCreate,
  JenkinsConnectionListResponse,
  JenkinsConnectionUpdate,
  JenkinsCredential,
  JenkinsCredentialIssue,
  JenkinsCredentialIssued,
  JenkinsJobBinding,
  JenkinsJobBindingCreate,
  JenkinsJobBindingListResponse,
  JenkinsJobBindingUpdate,
  JenkinsPage,
  JenkinsPublisher,
  JenkinsTestChallenge,
  JenkinsTestRequest,
} from '@/types/integrations/jenkins'
import type {
  SlackConnection,
  SlackConnectionAdmin,
  SlackConnectionCreate,
  SlackConnectionListResponse,
  SlackConnectionUpdate,
  SlackDestination,
  SlackDestinationCreate,
  SlackDestinationFilters,
  SlackDestinationListResponse,
  SlackDestinationUpdate,
  SlackPage,
} from '@/types/integrations/slack'
import { apiClient } from './client'
import { ifMatchHeaders } from './ifMatch'

const INTEGRATIONS = '/api/v1/integrations'
const SLACK = `${INTEGRATIONS}/slack`
const JENKINS = `${INTEGRATIONS}/jenkins`

export const integrations = {
  capabilities: {
    // Evidence and readiness, narrowed by provider and connection.
    list: async (
      filters: IntegrationCapabilityFilters = {}
    ): Promise<IntegrationCapabilityListResponse> =>
      (
        await apiClient.get<IntegrationCapabilityListResponse>(
          `${INTEGRATIONS}/capabilities/`,
          { params: filters }
        )
      ).data,
  },

  communication: {
    // One summary per connection and direction that has traffic to count.
    list: async (
      filters: IntegrationCommunicationFilters = {}
    ): Promise<IntegrationCommunicationListResponse> =>
      (
        await apiClient.get<IntegrationCommunicationListResponse>(
          `${INTEGRATIONS}/communication/`,
          { params: filters }
        )
      ).data,
  },

  receipts: {
    // One connection's receipts, newest first: states, outcomes and times,
    // never a payload, and only those a confined reader's projects resolved.
    list: async (
      filters: IntegrationReceiptFilters
    ): Promise<IntegrationReceiptListResponse> =>
      (
        await apiClient.get<IntegrationReceiptListResponse>(
          `${INTEGRATIONS}/receipts/`,
          { params: filters }
        )
      ).data,

    // 202 once a failed or discarded receipt that still holds its payload is
    // queued again; 409 `already_processed`, `not_settled` or `payload_purged`
    // for one that cannot run again.
    replay: async (
      receiptId: string,
      body: IntegrationReceiptReplayRequest
    ): Promise<IntegrationReceipt> =>
      (
        await apiClient.post<IntegrationReceipt>(
          `${INTEGRATIONS}/receipts/${receiptId}/replay/`,
          body
        )
      ).data,
  },

  slack: {
    listConnections: async (
      page: SlackPage = {}
    ): Promise<SlackConnectionListResponse> =>
      (
        await apiClient.get<SlackConnectionListResponse>(`${SLACK}/connections/`, {
          params: page,
        })
      ).data,

    getConnection: async (connectionId: string): Promise<SlackConnection> =>
      (await apiClient.get<SlackConnection>(`${SLACK}/connections/${connectionId}/`))
        .data,

    getConnectionAdmin: async (
      connectionId: string
    ): Promise<SlackConnectionAdmin> =>
      (
        await apiClient.get<SlackConnectionAdmin>(
          `${SLACK}/connections/${connectionId}/admin/`
        )
      ).data,

    // 201, answering with the administrator's projection.
    registerConnection: async (
      body: SlackConnectionCreate
    ): Promise<SlackConnectionAdmin> =>
      (await apiClient.post<SlackConnectionAdmin>(`${SLACK}/connections/`, body))
        .data,

    updateConnection: async (
      connectionId: string,
      body: SlackConnectionUpdate,
      ifMatch: number
    ): Promise<SlackConnectionAdmin> =>
      (
        await apiClient.patch<SlackConnectionAdmin>(
          `${SLACK}/connections/${connectionId}/`,
          body,
          ifMatchHeaders(ifMatch)
        )
      ).data,

    deleteConnection: async (connectionId: string, ifMatch: number): Promise<void> => {
      await apiClient.delete(
        `${SLACK}/connections/${connectionId}/`,
        ifMatchHeaders(ifMatch)
      )
    },

    verifyConnection: async (connectionId: string): Promise<SlackConnectionAdmin> =>
      (
        await apiClient.post<SlackConnectionAdmin>(
          `${SLACK}/connections/${connectionId}/verify/`
        )
      ).data,

    listDestinations: async (
      filters: SlackDestinationFilters = {}
    ): Promise<SlackDestinationListResponse> =>
      (
        await apiClient.get<SlackDestinationListResponse>(`${SLACK}/destinations/`, {
          params: filters,
        })
      ).data,

    getDestination: async (destinationId: string): Promise<SlackDestination> =>
      (
        await apiClient.get<SlackDestination>(
          `${SLACK}/destinations/${destinationId}/`
        )
      ).data,

    createDestination: async (
      body: SlackDestinationCreate
    ): Promise<SlackDestination> =>
      (await apiClient.post<SlackDestination>(`${SLACK}/destinations/`, body)).data,

    updateDestination: async (
      destinationId: string,
      body: SlackDestinationUpdate,
      ifMatch: number
    ): Promise<SlackDestination> =>
      (
        await apiClient.patch<SlackDestination>(
          `${SLACK}/destinations/${destinationId}/`,
          body,
          ifMatchHeaders(ifMatch)
        )
      ).data,

    deleteDestination: async (destinationId: string, ifMatch: number): Promise<void> => {
      await apiClient.delete(
        `${SLACK}/destinations/${destinationId}/`,
        ifMatchHeaders(ifMatch)
      )
    },

    // 409 while the destination is paused: a paused channel receives nothing,
    // a test post included.
    verifyDestination: async (destinationId: string): Promise<SlackDestination> =>
      (
        await apiClient.post<SlackDestination>(
          `${SLACK}/destinations/${destinationId}/verify/`
        )
      ).data,
  },

  jenkins: {
    listConnections: async (
      page: JenkinsPage = {}
    ): Promise<JenkinsConnectionListResponse> =>
      (
        await apiClient.get<JenkinsConnectionListResponse>(`${JENKINS}/connections/`, {
          params: page,
        })
      ).data,

    getConnection: async (connectionId: string): Promise<JenkinsConnection> =>
      (await apiClient.get<JenkinsConnection>(`${JENKINS}/connections/${connectionId}/`))
        .data,

    getConnectionAdmin: async (
      connectionId: string
    ): Promise<JenkinsConnectionAdmin> =>
      (
        await apiClient.get<JenkinsConnectionAdmin>(
          `${JENKINS}/connections/${connectionId}/admin/`
        )
      ).data,

    // 201 in `setup`; 409 when the name or a live base URL is already taken.
    registerConnection: async (
      body: JenkinsConnectionCreate
    ): Promise<JenkinsConnection> =>
      (await apiClient.post<JenkinsConnection>(`${JENKINS}/connections/`, body)).data,

    // A new base URL returns every credential under the instance to untested.
    updateConnection: async (
      connectionId: string,
      body: JenkinsConnectionUpdate,
      ifMatch: number
    ): Promise<JenkinsConnection> =>
      (
        await apiClient.patch<JenkinsConnection>(
          `${JENKINS}/connections/${connectionId}/`,
          body,
          ifMatchHeaders(ifMatch)
        )
      ).data,

    // 409 `prerequisites_unmet`, naming what setup still owes, until a
    // credential is active and a test has verified.
    enableConnection: async (
      connectionId: string,
      ifMatch: number
    ): Promise<JenkinsConnection> =>
      (
        await apiClient.post<JenkinsConnection>(
          `${JENKINS}/connections/${connectionId}/enable/`,
          undefined,
          ifMatchHeaders(ifMatch)
        )
      ).data,

    // Live failures are refused while tests are still admitted.
    disableConnection: async (
      connectionId: string,
      ifMatch: number
    ): Promise<JenkinsConnection> =>
      (
        await apiClient.post<JenkinsConnection>(
          `${JENKINS}/connections/${connectionId}/disable/`,
          undefined,
          ifMatchHeaders(ifMatch)
        )
      ).data,

    // Cannot be undone: every active credential is revoked, and the bindings
    // and history are kept.
    decommissionConnection: async (
      connectionId: string,
      ifMatch: number
    ): Promise<JenkinsConnection> =>
      (
        await apiClient.post<JenkinsConnection>(
          `${JENKINS}/connections/${connectionId}/decommission/`,
          undefined,
          ifMatchHeaders(ifMatch)
        )
      ).data,

    // 409 `in_use` while anything still refers to the instance.
    deleteConnection: async (connectionId: string, ifMatch: number): Promise<void> => {
      await apiClient.delete(
        `${JENKINS}/connections/${connectionId}/`,
        ifMatchHeaders(ifMatch)
      )
    },

    // Only the bindings into projects the caller may see, counted the same way.
    listBindings: async (
      filters: JenkinsBindingFilters = {}
    ): Promise<JenkinsJobBindingListResponse> =>
      (
        await apiClient.get<JenkinsJobBindingListResponse>(`${JENKINS}/bindings/`, {
          params: filters,
        })
      ).data,

    getBinding: async (bindingId: string): Promise<JenkinsJobBinding> =>
      (await apiClient.get<JenkinsJobBinding>(`${JENKINS}/bindings/${bindingId}/`)).data,

    // 409 when the instance already binds the job.
    createBinding: async (body: JenkinsJobBindingCreate): Promise<JenkinsJobBinding> =>
      (await apiClient.post<JenkinsJobBinding>(`${JENKINS}/bindings/`, body)).data,

    updateBinding: async (
      bindingId: string,
      body: JenkinsJobBindingUpdate,
      ifMatch: number
    ): Promise<JenkinsJobBinding> =>
      (
        await apiClient.patch<JenkinsJobBinding>(
          `${JENKINS}/bindings/${bindingId}/`,
          body,
          ifMatchHeaders(ifMatch)
        )
      ).data,

    deleteBinding: async (bindingId: string, ifMatch: number): Promise<void> => {
      await apiClient.delete(`${JENKINS}/bindings/${bindingId}/`, ifMatchHeaders(ifMatch))
    },

    // Every credential the instance has issued, newest first, as a bare list:
    // an administrator's read, and never a token.
    listCredentials: async (connectionId: string): Promise<JenkinsCredential[]> =>
      (
        await apiClient.get<JenkinsCredential[]>(`${JENKINS}/credentials/`, {
          params: { connection_id: connectionId },
        })
      ).data,

    // 201 with the token, which no later read carries, so only the caller's
    // reveal may hold it. A credential edits its instance's credential set, so
    // If-Match is the instance's version. 409 `decommissioned`, 422
    // `pull_requests_not_included`, and a binding's 404 for one of another
    // instance.
    issueCredential: async (
      body: JenkinsCredentialIssue,
      ifMatch: number
    ): Promise<JenkinsCredentialIssued> =>
      (
        await apiClient.post<JenkinsCredentialIssued>(
          `${JENKINS}/credentials/`,
          body,
          ifMatchHeaders(ifMatch)
        )
      ).data,

    // 201 with the replacement's token, answered as issue's is; the old token
    // keeps verifying through the overlap. 409 `credential_inactive`, or
    // `no_bindings` when none of the old scope is still bound.
    rotateCredential: async (
      credentialId: string,
      ifMatch: number
    ): Promise<JenkinsCredentialIssued> =>
      (
        await apiClient.post<JenkinsCredentialIssued>(
          `${JENKINS}/credentials/${credentialId}/rotate/`,
          undefined,
          ifMatchHeaders(ifMatch)
        )
      ).data,

    // Refused from this commit; 404 for one already revoked.
    revokeCredential: async (
      credentialId: string,
      ifMatch: number
    ): Promise<JenkinsCredential> =>
      (
        await apiClient.post<JenkinsCredential>(
          `${JENKINS}/credentials/${credentialId}/revoke/`,
          undefined,
          ifMatchHeaders(ifMatch)
        )
      ).data,

    // Evidence rather than an edit, so no If-Match: the challenge and the job
    // that answers it. 409 `decommissioned`, `credential_inactive` or
    // `binding_not_covered`.
    requestCredentialTest: async (
      credentialId: string,
      body: JenkinsTestRequest
    ): Promise<JenkinsTestChallenge> =>
      (
        await apiClient.post<JenkinsTestChallenge>(
          `${JENKINS}/credentials/${credentialId}/test/`,
          body
        )
      ).data,

    // A viewer's read, because it holds no secret: a Jenkins administrator
    // with read access copies the snippets from it.
    getPublisher: async (credentialId: string): Promise<JenkinsPublisher> =>
      (
        await apiClient.get<JenkinsPublisher>(
          `${JENKINS}/credentials/${credentialId}/publisher/`
        )
      ).data,

    // A project's failures, the latest occurrence first; 404 for a project
    // the caller may not see.
    listProjectBuilds: async (
      projectId: string,
      filters: JenkinsBuildFilters = {},
      signal?: AbortSignal
    ): Promise<JenkinsBuildEventListResponse> =>
      (
        await apiClient.get<JenkinsBuildEventListResponse>(
          `${JENKINS}/projects/${projectId}/builds/`,
          { params: filters, signal }
        )
      ).data,

    // The read a build frame names, confined as the history is.
    getBuild: async (buildEventId: string): Promise<JenkinsBuildEvent> =>
      (await apiClient.get<JenkinsBuildEvent>(`${JENKINS}/builds/${buildEventId}/`)).data,
  },
}
